import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormattedMessage, useIntl } from "react-intl";
import { useAuthStore } from "@/lib/stores/authStore";
import { useAudienceStore } from "@/lib/stores/audienceStore";
import { showToast } from "@/lib/utils/toast";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { useLocale } from "@/contexts/IntlContext";
import loginImage from "@/assets/exercise-background-photos/login-page-3.png";
import { AuthFormCard } from "@/components/AuthFormCard";
import { AuthHeader } from "@/components/AuthHeader";
import { AuthBackButton } from "@/components/AuthBackButton";
import { AuthErrorAlert } from "@/components/AuthErrorAlert";
import { AuthTextInput } from "@/components/AuthTextInput";
import { AuthPasswordInput } from "@/components/AuthPasswordInput";
import { AuthFooterLink } from "@/components/AuthFooterLink";
export const Route = createFileRoute("/auth/login")({
  component: LoginComponent,
});

function LoginComponent() {
  const navigate = useNavigate();
  const intl = useIntl();
  const { audience } = useAudienceStore();
  const { locale } = useLocale();
  const loginSchema = z.object({
    nickname: z
      .string()
      .min(1, intl.formatMessage({ id: "validation.nickname.required" })),
    password: z
      .string()
      .min(1, intl.formatMessage({ id: "validation.password.required" })),
  });

  type LoginFormData = z.infer<typeof loginSchema>;

  const {
    user,
    login,
    isLoading,
    error,
    clearError,
    redirectAfterAuth,
    setRedirectAfterAuth,
  } = useAuthStore();

  useEffect(() => {
    if (user && user.id) {
      const audienceLabel = user.audience
        ? intl.formatMessage({ id: `register.audience.${user.audience}` })
        : null;

      if (audienceLabel) {
        showToast.info(
          intl.formatMessage(
            { id: "toast.auth.alreadyLoggedInWithAudience" },
            { audience: audienceLabel }
          )
        );
      } else {
        showToast.info(
          intl.formatMessage({ id: "toast.auth.alreadyLoggedIn" })
        );
      }

      if (redirectAfterAuth) {
        navigate({ to: redirectAfterAuth });
        setRedirectAfterAuth(null);
      } else if (audience) {
        const targetUrl =
          audience === "CHILD" ? "/children/exercises" : "/adults/exercises";
        navigate({ to: targetUrl });
      }
    }
  }, [user, navigate, intl, redirectAfterAuth, setRedirectAfterAuth, audience]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      clearError();
      await login(data);
      showToast.success(
        intl.formatMessage(
          { id: "toast.auth.loginSuccess" },
          { username: data.nickname }
        )
      );
      if (redirectAfterAuth) {
        navigate({ to: redirectAfterAuth });
        setRedirectAfterAuth(null);
      } else if (audience) {
        const targetUrl =
          audience === "CHILD" ? "/children/exercises" : "/adults/exercises";
        navigate({ to: targetUrl });
      }
    } catch {
      // Error handling is managed in the store
    }
  };

  useEffect(() => {
    clearError();
  }, [clearError]);

  return (
    <div className="flex flex-col relative">
      <AuthHeader
        image={loginImage}
        imageAlt="Sign In"
        title={<FormattedMessage id="login.title" />}
        subtitle={<FormattedMessage id="login.dataProtected" />}
      />

      <AuthFormCard>
        <div className="flex flex-col">
          <AuthBackButton onClick={() => navigate({ to: "/app" })} />

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <AuthErrorAlert
                action={
                  error === "AUDIENCE_MISMATCH" ? (
                    <Link
                      to="/audience"
                      className="flex items-center gap-1 text-sm text-primary font-medium hover:underline"
                      onClick={() => clearError()}
                    >
                      {locale === "ar" ? (
                        <FaArrowRight className="w-3.5 h-3.5" />
                      ) : (
                        <FaArrowLeft className="w-3.5 h-3.5" />
                      )}

                      <FormattedMessage id="error.login.goBackToAudienceSelection" />
                    </Link>
                  ) : undefined
                }
              >
                {error === "AUDIENCE_MISMATCH"
                  ? intl.formatMessage({
                      id:
                        audience === "CHILD"
                          ? "error.login.audienceMismatch.selectedChild"
                          : "error.login.audienceMismatch.selectedAdult",
                    })
                  : error}
              </AuthErrorAlert>
            )}

            <AuthTextInput
              {...register("nickname")}
              placeholder={intl.formatMessage({
                id: "login.nickname.placeholder",
              })}
              disabled={isLoading}
              onFocus={clearError}
              error={errors.nickname?.message}
            />

            <AuthPasswordInput
              {...register("password")}
              placeholder={intl.formatMessage({
                id: "login.password.placeholder",
              })}
              disabled={isLoading}
              onFocus={clearError}
              error={errors.password?.message}
            />

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? (
                <FormattedMessage id="login.loggingIn" />
              ) : (
                <FormattedMessage id="login.login" />
              )}
            </Button>

            <AuthFooterLink
              promptId="login.noAccount"
              linkId="login.signUp"
              to="/auth/register"
            />
          </form>
        </div>
      </AuthFormCard>
    </div>
  );
}
